import { useEffect, useRef } from 'react';
import { ChevronLeft, Lock, Play, Shuffle, MoreHorizontal, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useColorExtraction } from '@/hooks/use-color-extraction';
import { useDynamicGlassBackground } from '@/hooks/use-dynamic-glass-background';
import { GlassCompatibleGradient } from '@/components/ui/glass-compatible-background';
import { hapticLight, hapticMedium } from '@/lib/haptics';
import { motion } from 'framer-motion';
import type { Audiobook, AudiobookPage } from '@/hooks/use-audiobooks';

interface AudiobookAlbumViewProps {
  audiobook: Audiobook;
  currentPage: AudiobookPage | null;
  isPlaying: boolean;
  isSubscribed: boolean;
  listenedPage?: number;
  onBack: () => void;
  onSelectPage: (page: AudiobookPage, audiobook: Audiobook) => void;
  onOpenPaywall: () => void;
}

export function AudiobookAlbumView({
  audiobook,
  currentPage,
  isPlaying,
  isSubscribed,
  listenedPage = 0,
  onBack,
  onSelectPage,
  onOpenPaywall,
}: AudiobookAlbumViewProps) {
  const isBookLocked = !isSubscribed && !audiobook.isPreview;
  const isPlayingThisBook = currentPage?.bookId === audiobook.id && isPlaying;
  const containerRef = useRef<HTMLDivElement>(null);
  const { colors } = useColorExtraction(audiobook.coverUrl);
  
  // Enable iOS 26 Safari glass effect with extracted color
  useDynamicGlassBackground(colors.dominant);

  // Scroll to top when audiobook changes
  useEffect(() => {
    containerRef.current?.scrollTo(0, 0);
  }, [audiobook.id]);

  const handlePlayAll = () => {
    hapticMedium();
    const firstPage = audiobook.pages[0];
    if (firstPage) {
      onSelectPage(firstPage, audiobook);
    }
  };

  const handleShuffle = () => {
    hapticLight();
    if (audiobook.pages.length === 0) return;
    const randomIndex = Math.floor(Math.random() * Math.min(4, audiobook.pages.length));
    const page = audiobook.pages[randomIndex];
    if (page) {
      onSelectPage(page, audiobook);
    }
  };

  return (
    <>
      {/* iOS 26 Safari glass-compatible background */}
      <GlassCompatibleGradient 
        topColor={colors.dominant} 
        midColor="rgba(0,0,0,0.98)" 
        bottomColor="#000000" 
      />
      
      <div 
        ref={containerRef} 
        className="fixed inset-0 flex flex-col overflow-y-auto"
      >
        {/* Header with back button */}
      <header 
        className="sticky top-0 z-20 px-4 pb-2 flex items-center"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)' }}
      >
        <button
          onClick={() => {
            hapticLight();
            onBack();
          }}
          className="p-2 -ml-2 text-white/80 active:scale-90 transition-transform"
          aria-label="Back"
        >
          <ChevronLeft className="w-7 h-7" />
        </button>
      </header>

      {/* Album Art - Large, centered */}
      <div className="flex justify-center px-12 pt-2 pb-6">
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="w-full max-w-[280px] aspect-square rounded-lg overflow-hidden"
          style={{ boxShadow: `0 25px 50px -12px ${colors.dominant}80` }}
        >
          <img
            src={audiobook.coverUrl}
            alt={audiobook.title}
            className="w-full h-full object-cover"
          />
        </motion.div>
      </div>

      {/* Album Info */}
      <div className="px-5 pb-4">
        <h1 className="text-white text-2xl font-bold tracking-tight">{audiobook.title}</h1>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
            <img
              src={audiobook.coverUrl}
              alt={audiobook.author}
              className="w-full h-full object-cover"
            />
          </div>
          <span className="text-white font-medium text-sm">{audiobook.author}</span>
        </div>
        <p className="text-white/50 text-sm mt-1">
          Audiobook • {audiobook.totalPages} pages
        </p>
      </div>

      {/* Action Row */}
      <div className="flex items-center justify-between px-5 pb-4">
        <div className="flex items-center gap-4">
          {/* Small album art */}
          <div className="w-9 h-9 rounded overflow-hidden">
            <img
              src={audiobook.coverUrl}
              alt={audiobook.title}
              className="w-full h-full object-cover"
            />
          </div>
          
          {/* Add button */}
          <button className="text-white/60 active:scale-90 transition-transform">
            <Plus className="w-6 h-6" strokeWidth={1.5} />
          </button>
          
          {/* More options */}
          <button className="text-white/60 active:scale-90 transition-transform">
            <MoreHorizontal className="w-6 h-6" />
          </button>
        </div>

        <div className="flex items-center gap-4">
          {/* Shuffle */}
          <button 
            onClick={handleShuffle}
            className="text-primary active:scale-90 transition-transform"
          >
            <Shuffle className="w-6 h-6" />
          </button>
          
          {/* Big green play button */}
          <button
            onClick={handlePlayAll}
            className="w-14 h-14 rounded-full bg-primary flex items-center justify-center active:scale-95 transition-transform shadow-lg"
          >
            <Play className="w-7 h-7 text-black ml-1" fill="currentColor" />
          </button>
        </div>
      </div>

      {/* Track list */}
      <div className="flex-1 px-5 pb-32">
        {(!audiobook.pages || audiobook.pages.length === 0) ? (
          <div className="text-center py-12 text-white/40">
            <p className="text-lg mb-1">No audio yet</p>
            <p className="text-sm">This audiobook will appear here once the MP3s are uploaded.</p>
          </div>
        ) : (
          <div className="space-y-1">
            {audiobook.pages.map((page) => {
              const isCurrent = currentPage?.id === page.id;
              const isLocked = isBookLocked && page.pageNumber > 4;

              return (
                <button
                  key={page.id}
                  onClick={() => {
                    hapticLight();
                    if (isLocked) {
                      onOpenPaywall();
                      return;
                    }
                    onSelectPage(page, audiobook);
                  }}
                  className="w-full flex items-center gap-3 py-3 active:opacity-70 transition-opacity"
                >
                  {/* Track image */}
                  <img
                    src={page.imageUrl}
                    alt={`Page ${page.pageNumber}`}
                    className="w-12 h-12 rounded object-cover shrink-0"
                  />

                  {/* Info */}
                  <div className="flex-1 min-w-0 text-left">
                    <p className={cn(
                      "font-medium truncate",
                      isCurrent ? "text-primary" : "text-white"
                    )}>
                      Page {page.pageNumber}
                    </p>
                    <p className="text-white/50 text-sm truncate">
                      {audiobook.author}
                    </p>
                  </div>

                  {/* Status */}
                  {isLocked ? (
                    <Lock className="w-5 h-5 text-white/30 shrink-0" />
                  ) : isCurrent && isPlaying ? (
                    <div className="flex items-end gap-0.5 h-4 shrink-0">
                      <span className="w-1 bg-primary animate-pulse rounded-full" style={{ height: '60%' }} />
                      <span className="w-1 bg-primary animate-pulse rounded-full" style={{ height: '100%', animationDelay: '150ms' }} />
                      <span className="w-1 bg-primary animate-pulse rounded-full" style={{ height: '40%', animationDelay: '300ms' }} />
                    </div>
                  ) : (
                    <button 
                      onClick={(e) => e.stopPropagation()}
                      className="text-white/40 p-1"
                    >
                      <MoreHorizontal className="w-5 h-5" />
                    </button>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
    </>
  );
}
